export interface Point {
  x: number;
  y: number;
}

/** Runs of consecutive points; a missing value breaks the line. */
export function segmentsOf(points: (Point | undefined)[]): Point[][] {
  const runs: Point[][] = [];
  let run: Point[] = [];
  for (const point of points) {
    if (point) run.push(point);
    else if (run.length) {
      runs.push(run);
      run = [];
    }
  }
  if (run.length) runs.push(run);
  return runs;
}

/**
 * Tangents of a monotone cubic (Fritsch–Carlson): a smooth line that never
 * overshoots a point, so a smoothed series still reads its true peaks.
 */
function monotoneTangents(run: Point[]): number[] {
  const n = run.length;
  const slopes: number[] = [];
  for (let i = 0; i < n - 1; i++)
    slopes.push((run[i + 1].y - run[i].y) / (run[i + 1].x - run[i].x || 1));
  const tangents = run.map((_, i) =>
    i === 0
      ? slopes[0]
      : i === n - 1
      ? slopes[n - 2]
      : slopes[i - 1] * slopes[i] <= 0
      ? 0
      : (slopes[i - 1] + slopes[i]) / 2,
  );
  for (let i = 0; i < n - 1; i++) {
    if (slopes[i] === 0) {
      tangents[i] = 0;
      tangents[i + 1] = 0;
      continue;
    }
    const a = tangents[i] / slopes[i];
    const b = tangents[i + 1] / slopes[i];
    const h = a * a + b * b;
    if (h > 9) {
      const t = 3 / Math.sqrt(h);
      tangents[i] = t * a * slopes[i];
      tangents[i + 1] = t * b * slopes[i];
    }
  }
  return tangents;
}

/** The path through one run, starting with a move to its first point. */
export function runPath(run: Point[], smooth: boolean): string {
  const [first] = run;
  let path = `M${first.x},${first.y}`;
  if (!smooth || run.length < 3) {
    for (const point of run.slice(1)) path += `L${point.x},${point.y}`;
    return path;
  }
  const tangents = monotoneTangents(run);
  for (let i = 0; i < run.length - 1; i++) {
    const a = run[i];
    const b = run[i + 1];
    const dx = (b.x - a.x) / 3;
    path +=
      `C${a.x + dx},${a.y + tangents[i] * dx} ` +
      `${b.x - dx},${b.y - tangents[i + 1] * dx} ${b.x},${b.y}`;
  }
  return path;
}

/** One run's line closed down to the baseline, for an area's fill. */
export function areaPath(run: Point[], smooth: boolean, base: number): string {
  const first = run[0];
  const last = run[run.length - 1];
  return `${runPath(run, smooth)}L${last.x},${base}L${first.x},${base}Z`;
}

/**
 * A bar from `y0` to `y1` (screen coordinates), rounded at the end away
 * from the baseline only.
 */
export function barPath(
  x: number,
  width: number,
  y0: number,
  y1: number,
  radius: number,
): string {
  const top = Math.min(y0, y1);
  const bottom = Math.max(y0, y1);
  const r = Math.max(0, Math.min(radius, width / 2, bottom - top));
  if (r === 0) return `M${x},${top}H${x + width}V${bottom}H${x}Z`;
  if (y1 <= y0)
    // Grows upward: round the top corners.
    return (
      `M${x},${bottom}V${top + r}Q${x},${top} ${x + r},${top}` +
      `H${x + width - r}Q${x + width},${top} ${x + width},${top + r}` +
      `V${bottom}Z`
    );
  return (
    `M${x},${top}H${x + width}V${bottom - r}` +
    `Q${x + width},${bottom} ${x + width - r},${bottom}` +
    `H${x + r}Q${x},${bottom} ${x},${bottom - r}Z`
  );
}

/**
 * A bar `h` tall from `x0` to `x1`, rounded at the end away from `x0`
 * (zero, or the shared axis of a two-sided chart).
 */
export function horizontalBarPath(
  x0: number,
  x1: number,
  y: number,
  h: number,
  radius: number,
): string {
  const left = Math.min(x0, x1);
  const right = Math.max(x0, x1);
  const r = Math.max(0, Math.min(radius, h / 2, right - left));
  if (r === 0) return barPath(left, right - left, y, y + h, 0);
  if (x1 >= x0)
    return (
      `M${left},${y}H${right - r}Q${right},${y} ${right},${y + r}` +
      `V${y + h - r}Q${right},${y + h} ${right - r},${y + h}H${left}Z`
    );
  return (
    `M${right},${y}V${y + h}H${left + r}Q${left},${y + h} ${left},${
      y + h - r
    }` + `V${y + r}Q${left},${y} ${left + r},${y}Z`
  );
}
